import envConfig from "../../../envConfig";

/**
 * Fetches the models list with pricing information. When the active
 * workspace is provided (id + workspace API key), the workspace's fine-tuned
 * models are appended by the backend with their registry ids
 * (ToothFairyAI/…) and tier pricing — required for serving/tracking custom
 * models correctly.
 * @param {object} [workspace] - { id, apiKey } of the active workspace
 * @returns {Promise<Object>} Models list with pricing data
 */
const getModelsList = async (workspace = null) => {
  try {
    const params = new URLSearchParams();
    if (workspace?.id) params.set("workspaceid", workspace.id);
    const queryString = params.toString();
    const response = await fetch(
      `${envConfig.REACT_APP_AI_REST_ENDPOINT}/models_list${
        queryString ? `?${queryString}` : ""
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(workspace?.apiKey ? { "x-api-key": workspace.apiKey } : {}),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch models list: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching models list:", error);
    throw error;
  }
};

/**
 * Convert USD to UoI (Units of Intelligence)
 * 1 UoI = 0.01 USD
 * @param {number} usdAmount - Amount in USD
 * @returns {number} Amount in UoI
 */
const usdToUoi = (usdAmount) => {
  if (usdAmount == null) return null;
  return usdAmount / 0.01;
};

/**
 * Format UoI for display
 * @param {number} uoi - Units of Intelligence
 * @returns {string} Formatted UoI string
 */
const formatUoi = (uoi) => {
  if (uoi == null) return "N/A";
  if (uoi < 1) return uoi.toFixed(4);
  if (uoi < 100) return uoi.toFixed(2);
  return Math.round(uoi).toLocaleString();
};

/**
 * Fetches and filters serverless models with their details
 * @param {boolean} enforceRegional - If true, fetches only regional models
 * @param {object} [workspace] - { id, apiKey } of the active workspace; when
 * given, the workspace's fine-tuned models are included (registry ids)
 * @returns {Promise<{functionCallingModels: Array, baseModels: Array, visionModels: Array}>}
 */
const getServerlessModelsForUI = async (
  enforceRegional = false,
  workspace = null
) => {
  try {
    const params = new URLSearchParams();
    if (enforceRegional) params.set("enforce_regional", "true");
    if (workspace?.id) params.set("workspaceid", workspace.id);
    const queryString = params.toString();
    const url = `${envConfig.REACT_APP_AI_REST_ENDPOINT}/models_list${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(workspace?.apiKey ? { "x-api-key": workspace.apiKey } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch models list: ${response.status}`);
    }

    const data = await response.json();
    const templates = data?.templates || {};

    // Filter for serverless models only
    const serverlessModels = Object.entries(templates).filter(
      ([, model]) => model?.deploymentType === "serverless"
    );

    // Transform to functionCallingModels schema
    const functionCallingModels = serverlessModels
      .filter(([, model]) => model?.toolCalling === true)
      .map(([modelId, model]) => ({
        name: model?.deprecated
          ? `${model?.name || modelId} (deprecated)`
          : model?.name || modelId,
        id: modelId,
        plannerAvailable: !!model?.plannerAvailable,
        ...(model?.reasoner && { reasoner: true }),
        ...(model?.interleaved && { interleaved: model.interleaved }),
      }));

    // Transform to baseModels schema
    const baseModels = serverlessModels.map(([modelId, model]) => {
      const baseModel = {
        name: model?.deprecated
          ? `${model?.name || modelId} (deprecated)`
          : model?.name || modelId,
        id: modelId,
        dynamicRouting: !!model?.dynamicRouting,
      };

      if (model?.tfOnly) {
        baseModel.tfOnly = true;
      }

      if (model?.maxTokens) {
        baseModel.maxTokens = model.maxTokens;
      }

      if (model?.reasoner) {
        baseModel.reasoner = true;
      }

      if (model?.plannerAvailable) {
        baseModel.plannerAvailable = true;
      }

      if (model?.supportsBrowser) {
        baseModel.supportsBrowser = true;
      }

      if (model?.interleaved) {
        baseModel.interleaved = model.interleaved;
      }

      return baseModel;
    });

    // Transform to visionModels schema
    const visionModels = serverlessModels
      .filter(([, model]) => model?.supportsVision === true)
      .map(([modelId, model]) => {
        const visionModel = {
          name: model?.deprecated
            ? `${
                model?.name ? `${model.name} vision` : `${modelId} vision`
              } (deprecated)`
            : model?.name
            ? `${model.name} vision`
            : `${modelId} vision`,
          id: modelId,
        };

        if (model?.tfOnly) {
          visionModel.tfOnly = true;
        }

        if (model?.interleaved) {
          visionModel.interleaved = model.interleaved;
        }

        return visionModel;
      });

    return {
      functionCallingModels,
      baseModels,
      visionModels,
    };
  } catch (error) {
    console.error("[modelsHelper] Error transforming models:", error);
    return {
      functionCallingModels: [],
      baseModels: [],
      visionModels: [],
    };
  }
};

const getExternalModelsForUI = async () => {
  try {
    const data = await getModelsList();
    const templates = data?.templates || {};

    const externalModels = Object.entries(templates).filter(
      ([, model]) => model?.deploymentType === "external"
    );

    const providers = [
      ...new Set(externalModels.map(([, model]) => model?.provider)),
    ];

    const models = externalModels
      .filter(([, model]) => model?.deprecated !== true)
      .map(([modelId, model]) => ({
        name: model?.name || modelId,
        id: modelId,
        provider: model?.provider,
        model_type: model?.model_type,
        ...(model?.toolCalling && { toolCalling: true }),
        ...(model?.reasoner && { reasoner: true }),
        ...(model?.supportsVision && { supportsVision: true }),
        ...(model?.supportsBrowser && { supportsBrowser: true }),
        ...(model?.interleaved && { interleaved: model.interleaved }),
        ...(model?.max_context_length && {
          maxContextLength: model.max_context_length,
        }),
      }));

    const deprecatedModels = externalModels
      .filter(([, model]) => model?.deprecated === true)
      .map(([modelId, model]) => ({
        name: `${model?.name || modelId} (deprecated)`,
        id: modelId,
        provider: model?.provider,
        model_type: model?.model_type,
        deprecated: true,
        ...(model?.toolCalling && { toolCalling: true }),
        ...(model?.reasoner && { reasoner: true }),
        ...(model?.supportsVision && { supportsVision: true }),
        ...(model?.supportsBrowser && { supportsBrowser: true }),
        ...(model?.interleaved && { interleaved: model.interleaved }),
      }));

    return {
      models,
      deprecatedModels,
      providers,
    };
  } catch (error) {
    console.error("[modelsHelper] Error fetching external models:", error);
    return {
      models: [],
      deprecatedModels: [],
      providers: [],
    };
  }
};

/**
 * Fetches trainable models for fine-tuning from the backend registry.
 * These are the models available for LoRA training via Unsloth/SageMaker.
 * @returns {Promise<Array>} Array of trainable model objects
 */
const getTrainableModelsForUI = async () => {
  try {
    const data = await getModelsList();
    return data?.trainableModels || [];
  } catch (error) {
    console.error("[modelsHelper] Error fetching trainable models:", error);
    return [];
  }
};

export default {
  getModelsList,
  usdToUoi,
  formatUoi,
  getServerlessModelsForUI,
  getExternalModelsForUI,
  getTrainableModelsForUI,
};
